import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, AlertTriangle, Send, BadgeCheck, CircleDashed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { FormAlert } from "@/components/common/Alert";
import { BrandMark } from "@/components/common/BrandMark";
import { useSettings } from "@/context/SettingsContext";
import { api, errorMessage } from "@/lib/api";
import { contrast, readableOn } from "@/lib/colour";

const HEX = /^#[0-9A-Fa-f]{6}$/;

const ColourField = ({ id, label, value, onChange, hint }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <div className="flex gap-2">
      <input type="color" aria-label={`Pick ${label.toLowerCase()}`} value={HEX.test(value) ? value : "#000000"} onChange={onChange} className="h-10 w-12 cursor-pointer rounded-xl border bg-card p-1" data-testid={`${id}-picker`} />
      <Input id={id} value={value} onChange={onChange} maxLength={7} data-testid={`${id}-input`} />
    </div>
    <p className="text-sm text-muted-foreground">{hint}</p>
  </div>
);

const Preview = ({ brand, action }) => {
  const ok = HEX.test(brand) && HEX.test(action);
  if (!ok) return null;
  const ratio = contrast("#FFFFFF", brand);
  return (
    <div className="space-y-3" data-testid="settings-colour-preview">
      <p className="text-sm font-medium">Live preview</p>
      <div className="flex overflow-hidden rounded-2xl border">
        <div className="w-28 space-y-2 p-3" style={{ background: brand }}>
          <div className="h-6 w-6 rounded-md bg-white/15" />
          {["Home", "Teams", "Prompts"].map((t, i) => <div key={t} className="rounded-md px-2 py-1 text-xs text-white" style={{ background: i === 0 ? "rgba(255,255,255,0.14)" : "transparent" }}>{t}</div>)}
        </div>
        <div className="flex-1 space-y-3 bg-background p-4">
          <div className="h-1 rounded-full" style={{ background: brand }} />
          <div className="rounded-2xl border bg-card p-3 shadow-soft">
            <p className="text-sm font-semibold">Compass</p>
            <p className="text-xs text-muted-foreground">Campaign strategist</p>
            <span className="mt-2 inline-flex rounded-lg px-3 py-1.5 text-xs font-medium" style={{ background: action, color: readableOn(action) }}>Run</span>
          </div>
        </div>
      </div>
      {ratio < 4.5 && (
        <p className="flex items-start gap-2 rounded-xl border border-warning/50 bg-warning/10 px-3 py-2 text-sm" role="status" data-testid="settings-contrast-warning">
          <AlertTriangle className="!h-4 !w-4 mt-0.5 shrink-0 text-warning" aria-hidden="true" />
          White text on this brand colour has a contrast of {ratio.toFixed(1)} to 1, below the 4.5 to 1 minimum. You can still save it.
        </p>
      )}
    </div>
  );
};

const EmailPanel = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/email-status").then(({ data }) => setSt(data)).catch(() => setSt(false));
  useEffect(() => { load(); }, []);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-test");
      if (data.sent) toast.success("Test email sent to you"); else toast.error("The test email did not send. Check the sender settings.");
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!st) return null;
  return (
    <section className="card-elevated max-w-3xl space-y-4 p-6 md:p-8" data-testid="settings-email-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Email sending</h2>
        {st.verified ? <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-2.5 py-0.5 text-xs font-medium text-success" data-testid="settings-email-badge"><BadgeCheck className="!h-3.5 !w-3.5" /> Verified</span>
          : <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground" data-testid="settings-email-badge"><CircleDashed className="!h-3.5 !w-3.5" /> Not verified</span>}
      </div>
      <p className="text-sm text-muted-foreground">
        {st.domain_connected ? `Sending from ${st.sender_address || "the sender address"} through your own domain.` : "No Fuse Capital sending domain is connected yet. Emails go from the platform address, with your sender address as reply-to."}
      </p>
      <Button type="button" variant="secondary" onClick={test} disabled={busy} data-testid="settings-send-test-email"><Send className="!h-4 !w-4" /> {busy ? "Sending" : "Send test email"}</Button>
      {!st.domain_connected && (
        <details className="rounded-xl border p-4">
          <summary className="cursor-pointer text-sm font-medium">DNS records to add for your own domain</summary>
          <p className="mt-2 text-sm text-muted-foreground">{st.note}</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="settings-dns-table">
              <thead><tr className="text-muted-foreground"><th className="py-1 pr-3 font-medium">Type</th><th className="py-1 pr-3 font-medium">Host</th><th className="py-1 pr-3 font-medium">Value</th><th className="py-1 font-medium">Purpose</th></tr></thead>
              <tbody>{st.dns_records.map((r) => <tr key={r.host + r.type} className="border-t align-top"><td className="py-2 pr-3">{r.type}{r.priority ? ` ${r.priority}` : ""}</td><td className="py-2 pr-3 font-mono text-xs">{r.host}</td><td className="break-all py-2 pr-3 font-mono text-xs">{r.value}</td><td className="py-2 text-muted-foreground">{r.purpose}</td></tr>)}</tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
};

export default function SettingsAdmin() {
  const { reload } = useSettings();
  const fileRef = useRef(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setForm({ ...data, digest_email: data.digest_email || "", sender_address: data.sender_address || "" })).catch((e) => setError(errorMessage(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.put("/admin/settings", {
        hub_name: form.hub_name, brand_colour: form.brand_colour, action_colour: form.action_colour, digest_email: form.digest_email || null, sender_address: form.sender_address || null,
      });
      await reload();
      toast.success("Settings saved");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post("/admin/settings/logo", body);
      await reload();
      toast.success("Logo updated");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (!form) return error ? <FormAlert>{error}</FormAlert> : <Skeleton className="h-64 rounded-xl" />;

  return (
    <div className="space-y-6">
      <form onSubmit={save} className="card-elevated max-w-5xl space-y-6 p-6 md:p-8" data-testid="admin-settings">
        <FormAlert testid="settings-error">{error}</FormAlert>
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="hub-name">Hub name</Label>
              <Input id="hub-name" value={form.hub_name} maxLength={80} onChange={set("hub_name")} data-testid="settings-hub-name-input" />
            </div>
            <ColourField id="settings-brand-colour" label="Brand colour" value={form.brand_colour} onChange={set("brand_colour")} hint="Sidebar, brand mark, header strip and focus accents, in both themes." />
            <ColourField id="settings-action-colour" label="Action colour" value={form.action_colour} onChange={set("action_colour")} hint="Primary buttons such as Run and Save." />
          </div>
          <Preview brand={form.brand_colour} action={form.action_colour} />
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="digest-email">Digest email address</Label>
            <Input id="digest-email" type="email" value={form.digest_email} onChange={set("digest_email")} placeholder="Defaults to the Owners" data-testid="settings-digest-email-input" />
            <p className="text-sm text-muted-foreground">Receives the daily feedback digest at 08:00 UK.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sender-address">Sender address</Label>
            <Input id="sender-address" type="email" value={form.sender_address} onChange={set("sender_address")} placeholder="Not set" data-testid="settings-sender-address-input" />
            <p className="text-sm text-muted-foreground">Used as the reply-to address today. Becomes the sender once a Fuse Capital sending domain is connected.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <BrandMark className="h-12 w-12" />
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" aria-label="Logo file" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="settings-logo-input" />
          <Button type="button" variant="secondary" onClick={() => fileRef.current?.click()} data-testid="settings-logo-upload-button"><Upload className="!h-4 !w-4" /> Upload logo</Button>
          <span className="text-sm text-muted-foreground">PNG, JPG or WebP, up to 2 MB.</span>
        </div>
        <Button type="submit" disabled={busy} data-testid="settings-save-button">{busy ? "Saving" : "Save settings"}</Button>
      </form>
      <EmailPanel />
    </div>
  );
}
